import React from "react";
import { paragraphs } from "./helpers";
import DOMPurify from "dompurify";

// iter-54: helper — detect if a chapter body is HTML (from the new
// rich editor) and render it via dangerouslySetInnerHTML so <p>, <b>,
// <ul>, <img> don't leak into the preview as literal characters.
const _RICH_RE = /<\s*(p|h[1-6]|ul|ol|li|div|strong|em|u|s|blockquote|br|img)\b/i;
function RichOrParagraphs({ text, indentClass = "" }) {
  const t = text || "";
  if (_RICH_RE.test(t)) {
    const clean = DOMPurify.sanitize(t, {
      ALLOWED_TAGS: ["p","br","span","div","strong","b","em","i","u","s",
                     "sub","sup","h2","h3","h4","ul","ol","li","a",
                     "blockquote","code","pre","hr","img"],
      ALLOWED_ATTR: ["href","title","rel","target","src","alt","class","style"],
    });
    return <div className={"rich-preview " + indentClass}
                dangerouslySetInnerHTML={{ __html: clean }}/>;
  }
  return paragraphs(t).map((p, i) => (
    <p key={i} className={indentClass}>{p}</p>
  ));
}
import { computeChapterLabels } from "./tocNumbering";

// -----------------------------------------------------------------------------
// BookPreviewPane — WYSIWYG of the assembled book. Pure content renderer,
// no modal / no header / no close button. Wrap in whatever chrome you need
// (side-by-side pane, modal, print preview, etc.).
//
// iter-36: honours per-chapter ``kind`` ("chapter" | "part" | "section") and
// Roman-numbered Parts, so the preview matches the exported PDF/EPUB layout
// instead of showing every row with a raw "01, 02, 03" prefix.
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// iter-80 — render the optional per-chapter subtopics list as a bulleted
// lead-in above the chapter body. Accepts either an array or a
// newline-separated string; strips common bullet glyphs pasted by authors.
// -----------------------------------------------------------------------------
function SubtopicsList({ subtopics, variant }) {
  const items = React.useMemo(() => {
    const raw = subtopics;
    if (!raw) return [];
    const source = Array.isArray(raw) ? raw : String(raw).split(/\r?\n/);
    return source
      .map((line) => (line == null ? "" : String(line))
        .replace(/^\s*[•·●○\-*\u2013\u2014]+\s*/, "")
        .trim())
      .filter(Boolean);
  }, [subtopics]);
  if (items.length === 0) return null;
  if (variant === "toc") {
    // Compact TOC variant — indented under the chapter entry, smaller
    // type, still bulleted so it reads like a mini outline.
    return (
      <ul className="list-disc pl-12 mt-1 text-xs text-[color:var(--text-muted)] space-y-0.5"
          data-testid="preview-toc-subtopics">
        {items.map((t, i) => <li key={i} className="leading-snug">{t}</li>)}
      </ul>
    );
  }
  return (
    <ul className="list-disc pl-6 my-4 mx-auto max-w-[32em] text-[color:var(--text)] space-y-1"
        data-testid="preview-subtopics">
      {items.map((t, i) => <li key={i} className="leading-snug">{t}</li>)}
    </ul>
  );
}

// -----------------------------------------------------------------------------
// iter-83 — Cover Live Mockup. Renders a small book-cover card at the
// top of the preview with:
//   - the current cover art (or a tasteful gradient fallback)
//   - the book title / subtitle / author overlaid using the current
//     design_controls cover typography
// Author sees cover-font tweaks resolve in real time.
// -----------------------------------------------------------------------------
function CoverMockup({ info, design, coverUrl, focalPoint }) {
  if (!info) return null;
  const dc = design || {};
  const c = dc._computed || {};
  const align = dc.cover_title_align || "center";
  // iter-98 — background-position honours the click-set focal point
  // so the mockup mirrors what the title-page PDF will render.
  const fp = focalPoint && typeof focalPoint === "object" ? focalPoint : {};
  const fpPos = (typeof fp.x === "number" && typeof fp.y === "number")
                    ? `${fp.x}% ${fp.y}%` : "center";

  const bg = coverUrl
    ? { backgroundImage: `url("${coverUrl}")`, backgroundSize: "cover", backgroundPosition: fpPos }
    : { backgroundImage: "linear-gradient(160deg, #2b2b28 0%, #4a3a2e 45%, #7a4b2f 100%)" };

  const overlayJustify = align === "left" ? "flex-start"
                              : align === "right" ? "flex-end" : "center";
  const textAlign = align;

  return (
    <div className="mx-auto mb-10" data-testid="preview-cover-mockup"
          style={{ maxWidth: "20rem" }}>
      <p className="uppercase text-[10px] tracking-[0.25em] text-[color:var(--text-muted)] text-center mb-2">
        § Cover mockup
      </p>
      <div className="relative aspect-[2/3] shadow-lg overflow-hidden book-cover cover-page"
            style={bg}>
        <div className="absolute inset-0 bg-black/25"/>
        <div className="relative h-full flex flex-col p-6"
              style={{ justifyContent: overlayJustify, alignItems: textAlign === "left" ? "flex-start" : textAlign === "right" ? "flex-end" : "center" }}>
          <div className="flex-1"/>
          {info.title && (
            <h3 className="cover-title text-white drop-shadow"
                data-testid="preview-cover-title"
                style={{
                  fontFamily: c.cover_title_font_stack,
                  fontSize: c.cover_title_size_pt ? `${c.cover_title_size_pt * 0.85}pt` : undefined,
                  textAlign,
                  lineHeight: 1.1,
                  margin: 0,
                }}>
              {info.title}
            </h3>
          )}
          {info.subtitle && (
            <p className="cover-subtitle text-white/90 drop-shadow mt-2"
                data-testid="preview-cover-subtitle"
                style={{
                  fontFamily: c.cover_subtitle_font_stack,
                  fontSize: c.cover_subtitle_size_pt ? `${c.cover_subtitle_size_pt * 0.85}pt` : undefined,
                  textAlign,
                  fontStyle: "italic",
                  margin: 0,
                }}>
              {info.subtitle}
            </p>
          )}
          <div className="flex-1"/>
          {info.author && (
            <p className="cover-author text-white/90 drop-shadow"
                data-testid="preview-cover-author"
                style={{
                  fontFamily: c.cover_author_font_stack,
                  fontSize: c.cover_author_size_pt ? `${c.cover_author_size_pt * 0.85}pt` : undefined,
                  textAlign,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  margin: 0,
                }}>
              {info.author}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function BookPreviewPane({ info, tocNote, intro, front, back, chapters,
                                 chapterNumberingStyle = "arabic",
                                 designControls,
                                 coverUrl,
                                 coverFocalPoint,
                                 layoutClass = "p-10 md:p-16 space-y-16 max-w-3xl mx-auto" }) {
  const front_incl = (front || []).filter((s) => s.include && (s.content || "").trim());
  const back_incl  = (back || []).filter((s) => s.include && (s.content || "").trim());
  const hasIntro = (intro || "").trim();
  const chapterLabels = computeChapterLabels(chapters, chapterNumberingStyle);
  // iter-97 — Cover art on the title page uses the resolved Design →
  // Cover → Cover size (Small / Medium / Large). Falls back to Medium
  // (2.6in × 3.5in ≈ 250px × 336px on screen) when the design snapshot
  // hasn't been resolved yet.
  const _dcCoverSize = (designControls?.cover_size || "m").toLowerCase();
  const COVER_SCREEN_PX = { s: 180, m: 250, l: 320 };
  const coverMaxPx = COVER_SCREEN_PX[_dcCoverSize] || COVER_SCREEN_PX.m;
  // iter-98 — click-to-set focal point. When set, the inline cover
  // switches from `contain` (letterbox) to `cover` and anchors the
  // pinned point so no face gets cropped off. Missing / empty →
  // classic centered fit.
  const _fp = coverFocalPoint && typeof coverFocalPoint === "object" ? coverFocalPoint : {};
  const hasFocal = typeof _fp.x === "number" && typeof _fp.y === "number";
  const inlineCoverStyle = hasFocal
    ? { objectFit: "cover", objectPosition: `${_fp.x}% ${_fp.y}%` }
    : { objectFit: "contain" };

  return (
    <div className={layoutClass} data-testid="book-preview-pane">
      {/* iter-83 — Cover Live Mockup. Shows the current cover art with
          the design-controlled typography overlaid so cover-font
          tweaks update in real time. iter-98 threads the focal point
          into the background positioning so pinning updates the
          mockup instantly. */}
      <CoverMockup info={info} design={designControls} coverUrl={coverUrl} focalPoint={_fp}/>
      {/* Title page — iter-97 reading order:
          Title → Subtitle → Book Cover → Author → Publisher */}
      <section className="text-center py-10" data-testid="preview-title-page">
        <h1 className="font-editorial text-5xl md:text-6xl leading-tight">
          {info.title || <em className="text-[color:var(--text-muted)]">(no title)</em>}
        </h1>
        {info.subtitle && <p className="mt-4 text-xl italic text-[color:var(--text-muted)]">{info.subtitle}</p>}
        {coverUrl && (
          <div className="mt-6 book-cover" data-testid="preview-title-page-cover">
            <img src={coverUrl} alt=""
                 className="mx-auto shadow-md"
                 style={{
                   maxWidth: `${coverMaxPx}px`,
                   maxHeight: `${Math.round(coverMaxPx * 1.35)}px`,
                   ...(hasFocal
                     ? { width: `${coverMaxPx}px`, height: `${Math.round(coverMaxPx * 1.35)}px` }
                     : {}),
                   ...inlineCoverStyle,
                 }}/>
          </div>
        )}
        <p className="mt-10 text-sm">by {info.author || <em className="text-[color:var(--text-muted)]">Anonymous</em>}</p>
        {info.publisher && <p className="mt-3 text-xs uppercase tracking-widest text-[color:var(--text-muted)]">{info.publisher}</p>}
        {info.imprint && <p className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">{info.imprint}</p>}
      </section>

      {/* Table of Contents */}
      <section data-testid="preview-toc">
        <h2 className="font-editorial text-3xl text-center mb-6">Contents</h2>
        {tocNote?.trim() && <p className="text-sm text-[color:var(--text-muted)] italic mb-4 text-center">{tocNote}</p>}
        <ul className="space-y-2 max-w-lg mx-auto text-sm">
          {front_incl.map((s) => (
            <li key={`toc-f-${s.key}`}><a href={`#sec-${s.key}`} className="hover:underline">{s.title}</a></li>
          ))}
          {hasIntro && <li><a href="#sec-intro" className="hover:underline">Introduction</a></li>}
          {chapters.map((c, i) => {
            const meta = chapterLabels[i];
            if (meta.kind === "part") {
              // Bold Part row — no chapter number, styled as a divider entry.
              return (
                <li key={`toc-ch-${i}`} className="pt-3 first:pt-0" data-testid={`toc-row-${i}`} data-kind="part">
                  <a href={`#sec-ch-${i}`} className="hover:underline font-semibold uppercase tracking-wider text-[color:var(--text)]">
                    {c.label?.trim() || meta.label || c.title}
                    {c.title && !c.title.toLowerCase().startsWith("part") && (
                      <span className="text-[color:var(--text-muted)] normal-case font-normal"> — {c.title.replace(/^part\s+[ivxlcdm\d]+\s*[:\-–—]?\s*/i, "")}</span>
                    )}
                  </a>
                </li>
              );
            }
            if (meta.kind === "section") {
              return (
                <li key={`toc-ch-${i}`} data-testid={`toc-row-${i}`} data-kind="section">
                  <a href={`#sec-ch-${i}`} className="hover:underline">
                    <span className="italic text-[color:var(--text-muted)] mr-2">§</span>
                    {c.label?.trim() || c.title || "Section"}
                  </a>
                </li>
              );
            }
            if (meta.kind === "appendix") {
              // iter-97 — Labeless entry (no §, no number).
              return (
                <li key={`toc-ch-${i}`} data-testid={`toc-row-${i}`} data-kind="appendix">
                  <a href={`#sec-ch-${i}`} className="hover:underline">
                    {c.label?.trim() || c.title || "Appendix"}
                  </a>
                </li>
              );
            }
            // Chapter row — numbered by computed label ("Chapter 3") instead
            // of the raw row index. Override label wins when provided.
            return (
              <li key={`toc-ch-${i}`} data-testid={`toc-row-${i}`} data-kind="chapter">
                <a href={`#sec-ch-${i}`} className="hover:underline">
                  <span className="font-mono text-[color:var(--text-muted)] mr-2">{String(meta.tocNumber).padStart(2, "0")}</span>
                  <span className="text-[color:var(--text-muted)] mr-1">{c.label?.trim() || meta.label}: </span>
                  {c.title || `Chapter ${meta.tocNumber}`}
                  {c.subtitle && <span className="text-[color:var(--text-muted)]"> — {c.subtitle}</span>}
                </a>
                {c.description && (
                  <div className="pl-8 text-xs text-[color:var(--text-muted)] italic">{c.description}</div>
                )}
                <SubtopicsList subtopics={c.subtopics} variant="toc"/>
              </li>
            );
          })}
          {back_incl.map((s) => (
            <li key={`toc-b-${s.key}`}><a href={`#sec-${s.key}`} className="hover:underline">{s.title}</a></li>
          ))}
        </ul>
      </section>

      {front_incl.map((s) => (
        <section key={`sec-${s.key}`} id={`sec-${s.key}`}>
          <h2 className="font-editorial text-3xl text-center mb-6">{s.title}</h2>
          <div className="font-reading text-base leading-relaxed space-y-3">
            <RichOrParagraphs text={s.content} indentClass="text-justify"/>
          </div>
        </section>
      ))}

      {hasIntro && (
        <section id="sec-intro">
          <h2 className="font-editorial text-3xl text-center mb-6">Introduction</h2>
          <div className="font-reading text-base leading-relaxed space-y-3">
            <RichOrParagraphs text={intro} indentClass="text-justify"/>
          </div>
        </section>
      )}

      {chapters.map((c, i) => {
        const meta = chapterLabels[i];
        if (meta.kind === "part") {
          // Full-page part divider — big Roman-numbered kicker, then the
          // Part's descriptive title. No body header, no "Chapter N".
          return (
            <section key={`sec-ch-${i}`} id={`sec-ch-${i}`} className="text-center py-14 border-t border-b border-[color:var(--border)]" data-testid={`preview-body-${i}`} data-kind="part">
              <p className="text-xs uppercase tracking-[0.5em] text-[color:var(--text-muted)]">
                {c.label?.trim() || meta.label}
              </p>
              <h2 className="font-editorial text-4xl md:text-5xl mt-3">
                {c.title?.replace(/^part\s+[ivxlcdm\d]+\s*[:\-–—]?\s*/i, "") || (c.label?.trim() || meta.label)}
              </h2>
              {c.subtitle && <p className="mt-3 italic text-[color:var(--text-muted)]">{c.subtitle}</p>}
              {/* iter-83 — subtopics only render in the TOC */}
              {c.content?.trim() && (
                <div className="font-reading text-base leading-relaxed space-y-3 mt-6 text-left">
                  <RichOrParagraphs text={c.content} indentClass="text-justify"/>
                </div>
              )}
            </section>
          );
        }
        if (meta.kind === "section") {
          // Un-numbered titled section — Resources / Interlude / Bonus.
          return (
            <section key={`sec-ch-${i}`} id={`sec-ch-${i}`} data-testid={`preview-body-${i}`} data-kind="section">
              <h2 className="font-editorial text-3xl text-center mb-6">{c.label?.trim() || c.title || "Section"}</h2>
              {c.subtitle && <p className="text-center italic text-[color:var(--text-muted)] mb-6">{c.subtitle}</p>}
              {/* iter-83 — subtopics only render in the TOC */}
              <div className="font-reading text-base leading-relaxed space-y-3">
                {c.content?.trim()
                  ? <RichOrParagraphs text={c.content} indentClass="text-justify"/>
                  : <p className="italic text-[color:var(--text-muted)] text-center">(empty section)</p>
                }
              </div>
            </section>
          );
        }
        if (meta.kind === "appendix") {
          // iter-97 — Labeless appendix body. No kicker, no numbering.
          return (
            <section key={`sec-ch-${i}`} id={`sec-ch-${i}`} data-testid={`preview-body-${i}`} data-kind="appendix">
              <h2 className="font-editorial text-3xl text-center mb-6">{c.label?.trim() || c.title || "Appendix"}</h2>
              {c.subtitle && <p className="text-center italic text-[color:var(--text-muted)] mb-6">{c.subtitle}</p>}
              <div className="font-reading text-base leading-relaxed space-y-3">
                {c.content?.trim()
                  ? <RichOrParagraphs text={c.content} indentClass="text-justify"/>
                  : <p className="italic text-[color:var(--text-muted)] text-center">(empty appendix)</p>
                }
              </div>
            </section>
          );
        }
        return (
          <section key={`sec-ch-${i}`} id={`sec-ch-${i}`} data-testid={`preview-body-${i}`} data-kind="chapter">
            <p className="text-center text-xs uppercase tracking-[0.3em] text-[color:var(--text-muted)]">{c.label?.trim() || meta.label}</p>
            <h2 className="font-editorial text-4xl md:text-5xl text-center mt-2 mb-2">{c.title || meta.label}</h2>
            {c.subtitle && <p className="text-center italic text-[color:var(--text-muted)] mb-6">{c.subtitle}</p>}
            {/* iter-83 — subtopics only render in the TOC */}
            <div className="font-reading text-base leading-relaxed space-y-3 mt-6">
              {c.content?.trim()
                ? <RichOrParagraphs text={c.content} indentClass="text-justify"/>
                : <p className="italic text-[color:var(--text-muted)] text-center">(empty chapter — paste content in the composer)</p>
              }
            </div>
          </section>
        );
      })}

      {back_incl.map((s) => (
        <section key={`sec-${s.key}`} id={`sec-${s.key}`}>
          <h2 className="font-editorial text-3xl text-center mb-6">{s.title}</h2>
          <div className="font-reading text-base leading-relaxed space-y-3">
            <RichOrParagraphs text={s.content} indentClass="text-justify"/>
          </div>
        </section>
      ))}
    </div>
  );
}
